import { useSignal } from '@preact/signals';

import type { MemberRemovalView } from '@amumin/api-client';
import { t } from '@amumin/i18n';
import { Button, showToast } from '@amumin/ui';
import { Avatar, Icon } from '@amumin/ui-core';

import { api, errorMessage, isApiError } from '../api.js';
import { formatDateKeyLong, formatInstantFriendly } from '../lib/dates.js';
import { StepUpCancelled, withStepUp } from '../lib/stepUp.js';
import { useLoad } from '../lib/ui.js';
import {
  appText,
  bumpData,
  context,
  handleAuthLoss,
  household,
  loadHousehold,
  messages,
} from '../state/session.js';
import { householdDeletion, refreshDeletion } from './deletionState.js';
import styles from './privacy.module.css';

// Gedeelde stukjes van de privacyrechten in de ouderapp (0036; DESIGN §8.PR): foutteksten, de lijst
// "Uit het gezin gehaald" met Terugzetten (op Gezin en op Jouw gegevens) en de banner in de schil.

/** Rol van de ingelogde ouder in het actieve gezin. */
export function activeRole(): string | undefined {
  const ctx = context.value;
  return ctx?.households.find((x) => x.id === ctx.active_household_id)?.role;
}

/** Eén regel tekst voor een fout van de privacy-API (eigen codes eerst, dan de gewone). */
export function privacyErrorText(e: unknown): string {
  const p = appText.value.privacyRights;
  if (isApiError(e)) {
    switch (e.code) {
      case 'is_owner':
        return p.errorIsOwner;
      case 'is_self':
        return p.errorIsSelf;
      case 'needs_owner':
        return p.errorNeedsOwner;
      case 'window_closed':
        return p.errorWindowClosed;
      case 'confirm_mismatch':
        return p.errorConfirmName;
      case 'already_removed':
        return p.errorAlreadyRemoved;
      case 'deletion_unavailable':
        return p.errorDeletionUnavailable;
      case 'expired':
      case 'already_downloaded':
        return p.errorExportGone;
      default:
        break;
    }
  }
  return errorMessage(e, messages.value);
}

/** Toast bij een fout; een geannuleerde step-up of een verlopen sessie blijft stil. */
export function toastPrivacyError(e: unknown): void {
  if (e instanceof StepUpCancelled) return;
  if (handleAuthLoss(e)) return;
  showToast({ kind: 'error', message: privacyErrorText(e) });
}

/** Terugzetten (step-up) met de juiste melding; ververst gezin en schermen. */
export async function undoRemoval(r: MemberRemovalView): Promise<boolean> {
  const p = appText.value.privacyRights;
  try {
    const res = await withStepUp(() => api.privacy.removals.undo(r.id));
    await loadHousehold(true).catch(() => undefined);
    bumpData();
    showToast({
      kind: 'success',
      message: t(r.had_login && res.restored_logins === 0 ? p.undoneNoLogin : p.undone, {
        name: r.display_name,
      }),
    });
    return true;
  } catch (e) {
    toastPrivacyError(e);
    return false;
  }
}

/**
 * "Uit het gezin gehaald": wie nog terug te zetten is, met de datum tot wanneer. Niets als de lijst
 * leeg is (geen lege kaart). Alleen voor ouders (de API geeft anders 403).
 */
export function RemovedMembers({ heading = true }: { heading?: boolean }) {
  const a = appText.value;
  const p = a.privacyRights;
  const tz = household.value?.timezone;
  const list = useLoad(() => api.privacy.removals.list());
  const busy = useSignal<string | null>(null);
  const items = list.data.value?.items ?? [];
  if (items.length === 0) return null;
  return (
    <section class={styles.removed} aria-labelledby="removed-title" data-name="removed-members">
      {heading ? (
        <h2 class="section-title" id="removed-title">
          {p.removedListTitle}
        </h2>
      ) : (
        <h3 class={styles.subTitle} id="removed-title">
          {p.removedListTitle}
        </h3>
      )}
      <ul class={styles.removedList}>
        {items.map((r) => (
          <li
            key={r.id}
            class={styles.removedRow}
            data-name="removed-row"
            data-member={r.member_id}
          >
            <span class={styles.faded}>
              <Avatar
                name={r.display_name}
                colorKey={r.color_key}
                icon={r.avatar_icon}
                size="chip"
                decorative
              />
            </span>
            <span class={styles.removedBody}>
              <strong>{r.display_name}</strong>
              <span class="muted small">
                {t(p.removedUntil, {
                  time: formatInstantFriendly(r.undo_until, messages.value, tz),
                })}
                {r.had_login ? ` · ${p.removedHadLogin}` : ''}
              </span>
            </span>
            <Button
              name="removal-undo"
              size="small"
              icon="undo-2"
              busy={busy.value === r.id}
              onPress={() => {
                if (busy.value) return;
                busy.value = r.id;
                void undoRemoval(r).finally(() => {
                  busy.value = null;
                  void list.reload();
                });
              }}
            >
              {p.undo}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Banner bovenaan de app zolang het gezinsbord gepland staat om verwijderd te worden (DESIGN
 * §8.PR.4): rustig, voor iedereen; de eigenaar kan het hier meteen tegenhouden.
 */
export function DeletionBanner() {
  const d = householdDeletion.value;
  const busy = useSignal(false);
  if (!d) return null;
  const a = appText.value;
  const p = a.privacyRights;
  const isOwner = activeRole() === 'owner';
  const cancel = async () => {
    if (busy.value) return;
    busy.value = true;
    try {
      await api.privacy.deletion.cancel();
      await refreshDeletion();
      showToast({ kind: 'success', message: p.cancelled });
    } catch (e) {
      toastPrivacyError(e);
    } finally {
      busy.value = false;
    }
  };
  return (
    <div class={styles.banner} role="status" data-name="deletion-banner">
      <Icon id="calendar-x" size={20} />
      <span class={styles.bannerText}>
        <strong>{t(p.banner, { date: formatDateKeyLong(d.on, messages.value) })}</strong>
        {isOwner ? null : <span class="muted small"> {p.bannerOthers}</span>}
      </span>
      <span class={styles.bannerActions}>
        {isOwner ? (
          <Button
            name="deletion-banner-cancel"
            size="small"
            variant="primary"
            busy={busy.value}
            onPress={() => void cancel()}
          >
            {p.bannerOwner}
          </Button>
        ) : null}
        <a class={styles.bannerLink} href="/instellingen/gegevens" data-name="deletion-banner-more">
          {p.bannerMore}
        </a>
      </span>
    </div>
  );
}
